import { Key } from 'react';
import { Radio, RadioGroup } from '@ds';
import { columns } from './config';
import { DonorType } from '@/api/apis/case';
import { CSAssignmentCandidate } from '@/api/apis/resources';
import { Hub } from '@/api/apis/facilities';
import { CS_CERTIFICATE_TYPE } from '@/utils/helpers';
import { getHubNameByHubId, getRadioDisabled } from './utils';
import {
  LocationCell,
  MatchCell,
  OrgansLastCall,
  ReservationCondition,
  ReserveButton,
  ResourceNameCell,
  TrialsCell,
} from '../components';
import { CertificateOrgans } from './CertificateOrgans';

export const renderCSTableCell =
  (
    isLogistics: boolean,
    donorType: DonorType,
    onReserve: (csCandidate: CSAssignmentCandidate) => void,
    hubs?: Hub[],
    isSwap?: boolean,
    caseName?: string,
    applyGoTime?: boolean,
  ) =>
  (csData: CSAssignmentCandidate & { isSelected?: boolean }, key: Key) => {
    const {
      employeeId,
      isSelected,
      baseLocation,
      employeeFullName,
      employeeProfilePhoto,
      shiftCertificates,
      employeeTrialCertificates,
      employeeCertificates,
      lastCallDays,
      lastTrackedLocation,
      match,
      onCallAfterTargetMinutes,
      onCallBeforeTargetMinutes,
      onRestEndsBeforeTargetMinutes,
      onRestStartsAfterTargetMinutes,
      conflictedReservation,
      conflictedRest,
      days30Stats,
      lastCertificationAtHubInfo,
      employeeShadowOrganCertificates,
      employeeProctorOrganCertificates,
      shiftTimeGroup,
    } = csData;

    const handleReserve = () => {
      onReserve(csData);
    };

    switch (key) {
      case columns.name.key:
        return <ResourceNameCell fullName={employeeFullName} photoPath={employeeProfilePhoto} />;
      case columns.organLastCall.key:
        return (
          <OrgansLastCall
            isClinicalSpecialist
            donorType={donorType}
            shiftCertificates={shiftCertificates}
            employeeCertificates={employeeCertificates}
            lastCallDaysByOrgan={lastCallDays.byOrgan}
            lastCallDaysByDonorAndOrgan={lastCallDays.byDonorAndOrgan}
          />
        );
      case columns.proctorShadowOrgans.key:
        return (
          <div className="flex flex-col gap-1">
            <CertificateOrgans type={CS_CERTIFICATE_TYPE.PROCTOR} organs={employeeProctorOrganCertificates} />
            <CertificateOrgans type={CS_CERTIFICATE_TYPE.SHADOW} organs={employeeShadowOrganCertificates} />
          </div>
        );
      case columns.trialCertificates.key:
        return <TrialsCell trialCertificates={employeeTrialCertificates} />;
      case columns.location.key:
        return (
          <LocationCell
            locationName={getHubNameByHubId(baseLocation, hubs)}
            lastTrackedLocation={lastTrackedLocation}
          />
        );
      case columns.thirtyDays.key:
        return <span className="text-m-r">{days30Stats.total} </span>;
      case columns.match.key:
        return <MatchCell matchType={match.matchType} />;
      case columns.reservationCondition.key:
        return (
          <ReservationCondition
            lastCertificationAtHubInfo={lastCertificationAtHubInfo}
            conflictReservation={conflictedReservation}
            conflictRest={conflictedRest}
            match={match}
            onCallAfterTargetMinutes={onCallAfterTargetMinutes}
            onCallBeforeTargetMinutes={onCallBeforeTargetMinutes}
            onRestEndsBeforeTargetMinutes={onRestEndsBeforeTargetMinutes}
            onRestStartsAfterTargetMinutes={onRestStartsAfterTargetMinutes}
            caseName={caseName}
            applyGoTime={applyGoTime}
            shiftTimeGroup={shiftTimeGroup}
          />
        );
      case columns.reserve.key:
        return !isSwap ? (
          <ReserveButton
            onReserve={handleReserve}
            isSelected={isSelected}
            isLogistics={isLogistics}
            assignActionType={match.availableActionType}
          />
        ) : (
          <RadioGroup onValueChange={handleReserve} value={employeeId} isDisabled={getRadioDisabled(match)}>
            <Radio size="lg" value={isSelected ? 'true' : 'false'} data-selected={isSelected} />
          </RadioGroup>
        );
      default:
        return null;
    }
  };
